'use client';

import { useEffect } from 'react';
import { Button, Signage } from '@kcd/design-system';

import { HttpError } from '~/api/client';
import { TrackPage } from '~/app/common/components';
import { useGoHome } from '~/app/hooks';
import { OAIApiError } from '~/libs/openApiError';
import { logError } from '~/utils/datadog';
import {
  closeCurrentModal,
  openDialogAsync,
  openModal,
} from '~/utils/mobileApp';
import { BUSINESS_REGISTER_URL } from '~/utils/url';

import { DataErrorImg } from '~/assets/img/common';

type Props = {
  error: Error & { digest?: string };
  reset: () => void;
};

function getStatusCode(error: Error): number | undefined {
  if (error instanceof HttpError) {
    return error.status;
  }
  if (error instanceof OAIApiError) {
    return error.statusCode;
  }
  return undefined;
}

function ServiceMaintenancePage({ reset }: { reset: () => void }) {
  return (
    <>
      <TrackPage page="캐시노트마켓/에러/503" />
      <Signage
        verticalCenter
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
        marginTop={0 as any}
        image={() => (
          <img src={DataErrorImg.src} className="size-64" alt="error guide" />
        )}
        className="!h-screen"
        title="현재 서비스 점검중입니다."
        description={
          <p className="text-hint typo-caption100-regular">
            잠시 후 다시 시도해주세요.
          </p>
        }
        action={
          <Button
            size="sm"
            className="-mt-16"
            variant="outline"
            onClick={reset}
          >
            다시 시도
          </Button>
        }
      />
    </>
  );
}

function NonLoginErrorContent() {
  return (
    <>
      <TrackPage page="캐시노트마켓/에러/401" />
      <Signage
        verticalCenter
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
        marginTop={0 as any}
        image={() => (
          <img src={DataErrorImg.src} className="size-64" alt="error guide" />
        )}
        title="일시적인 오류가 발생했습니다."
        description={
          <p className="text-hint typo-caption100-regular">
            서비스 이용에 불편을 드려 죄송합니다. <br /> 일시적인 장애이거나
            네트워크 문제일 수 있습니다.
            <br /> 잠시 후 다시 시도해주세요.
          </p>
        }
        action={
          <Button
            size="sm"
            className="-mt-16"
            variant="outline"
            onClick={closeCurrentModal}
          >
            되돌아가기
          </Button>
        }
      />
    </>
  );
}

function C1UserErrorContent() {
  useEffect(() => {
    void (async () => {
      await openDialogAsync({
        type: 'action',
        title: '사업장 등록',
        description:
          '캐시노트 마켓은 사업장이 등록된 사장님만 이용할 수 있어요',
        confirmText: '사업장 등록하기',
      });

      openModal({
        url: BUSINESS_REGISTER_URL,
      });
    })();
  }, []);

  return (
    <>
      <TrackPage page="캐시노트마켓/에러/403" />
      <Signage
        verticalCenter
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
        marginTop={0 as any}
        image={() => (
          <img src={DataErrorImg.src} className="size-64" alt="error guide" />
        )}
        title="일시적인 오류가 발생했습니다."
        description={
          <p className="text-hint typo-caption100-regular">
            서비스 이용에 불편을 드려 죄송합니다. <br /> 일시적인 장애이거나
            네트워크 문제일 수 있습니다.
            <br /> 잠시 후 다시 시도해주세요.
          </p>
        }
        action={
          <Button
            size="sm"
            className="-mt-16"
            variant="outline"
            onClick={closeCurrentModal}
          >
            되돌아가기
          </Button>
        }
      />
    </>
  );
}

function DefaultErrorContent({ reset }: { reset: () => void }) {
  const goHome = useGoHome('replace');

  return (
    <>
      <TrackPage page="캐시노트마켓/에러" />
      <Signage
        verticalCenter
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
        className="!mt-0 !h-screen"
        image={() => (
          <img src={DataErrorImg.src} className="size-64" alt="error guide" />
        )}
        title="일시적인 오류가 발생했습니다."
        description={
          <p className="text-hint typo-caption100-regular">
            서비스 이용에 불편을 드려 죄송합니다. <br /> 일시적인 장애이거나
            네트워크 문제일 수 있습니다.
            <br /> 잠시 후 다시 시도해주세요.
          </p>
        }
        action={
          <div className="-mt-16 flex gap-8">
            <Button size="sm" variant="outline" onClick={reset}>
              다시 시도
            </Button>
            <Button size="sm" variant="outline" onClick={goHome}>
              캐시노트마켓 홈으로 이동
            </Button>
          </div>
        }
      />
    </>
  );
}

export default function ErrorPage({ error, reset }: Props) {
  const statusCode = getStatusCode(error);

  useEffect(() => {
    logError(error);
  }, [error]);

  if (statusCode === 503) {
    return <ServiceMaintenancePage reset={reset} />;
  }

  if (statusCode === 401) {
    return <NonLoginErrorContent />;
  }

  if (statusCode === 403) {
    return <C1UserErrorContent />;
  }

  return <DefaultErrorContent reset={reset} />;
}
